import { z } from 'zod';

// Regex patterns for validation
export const patterns = {
  cpf: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
  cnpj: /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/,
  phone: /^\(\d{2}\)\s\d{4,5}-\d{4}$/,
  cep: /^\d{5}-\d{3}$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};

// Formatters for input masks
export const formatters = {
  cpf: (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  },

  cnpj: (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  },

  phone: (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 11) {
      return numbers.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d{4})$/, '$1-$2');
    }
    return numbers.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d{4})$/, '$1-$2');
  },

  cep: (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
  },
};

// Unformat values (remove masks)
export const unformat = (value: string): string => {
  return value.replace(/\D/g, '');
};

// Zod schemas for validation
export const createValidationSchema = <T extends z.ZodRawShape>(schema: T) => {
  return z.object(schema).strict();
};

export const validateField = <T extends z.ZodRawShape>(
  schema: z.ZodObject<T>,
  field: keyof T,
  value: any
): string | null => {
  try {
    schema.shape[field].parse(value);
    return null;
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.errors[0]?.message || null;
    }
    return null;
  }
};

export const validateForm = <T extends z.ZodRawShape>(
  schema: z.ZodObject<T>,
  values: Record<string, any>
): Record<string, string> => {
  try {
    schema.parse(values);
    return {};
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.errors.reduce((acc, err) => {
        const field = err.path[0] as string;
        acc[field] = err.message;
        return acc;
      }, {} as Record<string, string>);
    }
    return {};
  }
};
